import {useMemo} from 'react'
import {HttpGetAccessesDetailResponse} from '@/features/accesses/http/accesses.http'
import {calculateTotalAccessInAbsence} from '@/features/projects/utils'

/**
 * Custom hook to calculate various metrics related to planned accesses
 * @param accesses The planned accesses data from the API
 * @returns Calculated metrics for planned accesses
 */
export const useAccessesCalculations = (accesses: HttpGetAccessesDetailResponse | undefined) => {
    // Calculate total accesses in absence (non in-person)
    const totalAccessInAbsence = useMemo(() => {
        return accesses
            ? calculateTotalAccessInAbsence({
                  ...accesses,
                  in_person_accesses: accesses.in_person_accesses.map(access => ({
                      ...access,
                      frequency: null,
                      accessName: access.accessType
                  })), // TODO: remove this once the API is updated
                  non_in_person_accesses: accesses.non_in_person_accesses.map(access => ({
                      ...access,
                      frequency: null,
                      accessName: access.accessType
                  })), // TODO: remove this once the API is updated
                  treatments: accesses.treatments.map(treatment => ({
                      ...treatment,
                      frequency: null,
                      accessName: treatment.accessType
                  })) // TODO: remove this once the API is updated
              })
            : 0
    }, [accesses])

    // Calculate total treatments (all frequencies combined)
    const totalTreatments = useMemo(() => {
        return accesses ? accesses.treatments.reduce((sum, item) => sum + item.numberOfAccesses, 0) : 0
    }, [accesses])

    // Calculate total single accesses
    const totalSingleAccesses = useMemo(() => {
        return accesses ? accesses.in_person_accesses.reduce((sum, item) => sum + item.numberOfAccesses, 0) : 0
    }, [accesses])

    // Calculate total unaccounted accesses
    const totalUnaccountedAccesses = useMemo(() => {
        return accesses ? accesses.unaccounted_accesses.reduce((sum, item) => sum + item.numberOfAccesses, 0) : 0
    }, [accesses])

    // Calculate total accesses across all types
    const totalAccesses = useMemo(() => {
        return totalTreatments + totalSingleAccesses + totalUnaccountedAccesses + totalAccessInAbsence
    }, [totalTreatments, totalSingleAccesses, totalUnaccountedAccesses, totalAccessInAbsence])

    // Calculate total accesses in presence (in-person)
    const totalAccessesInPresence = useMemo(() => {
        return totalAccesses - totalAccessInAbsence
    }, [totalAccesses, totalAccessInAbsence])

    return {
        totalAccesses,
        totalAccessInAbsence,
        totalAccessesInPresence,
        totalTreatments,
        totalSingleAccesses,
        totalUnaccountedAccesses
    }
}
